import { reactive } from 'vue'
import en from '@/locales/en.json'
import ro from '@/locales/ro.json'

const LOCALE_STORAGE_KEY = 'drvn-locale'
const FALLBACK_LOCALE = 'en'

const messages = {
  en,
  ro,
}

const localeMeta = [
  { code: 'en', labelKey: 'language.en' },
  { code: 'ro', labelKey: 'language.ro' },
]

function normalizeLocale(value) {
  if (!value) return FALLBACK_LOCALE
  const normalized = String(value).trim().toLowerCase().slice(0, 2)
  return messages[normalized] ? normalized : FALLBACK_LOCALE
}

function detectInitialLocale() {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored) return normalizeLocale(stored)
  }

  if (typeof navigator !== 'undefined') {
    return normalizeLocale(navigator.language)
  }

  return FALLBACK_LOCALE
}

function getMessageValue(locale, key) {
  return key.split('.').reduce((value, segment) => {
    if (value && typeof value === 'object') {
      return value[segment]
    }
    return undefined
  }, messages[locale])
}

function interpolate(message, params = {}) {
  if (typeof message !== 'string') return message
  return message.replace(/\{(\w+)\}/g, (_, token) => {
    if (Object.prototype.hasOwnProperty.call(params, token)) {
      return String(params[token])
    }
    return `{${token}}`
  })
}

export const i18nState = reactive({
  locale: detectInitialLocale(),
})

export function t(key, params = {}) {
  const localized = getMessageValue(i18nState.locale, key)
  const fallback = getMessageValue(FALLBACK_LOCALE, key)
  const message = localized ?? fallback ?? key
  return interpolate(message, params)
}

export function setLocale(locale) {
  const normalized = normalizeLocale(locale)
  i18nState.locale = normalized

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
  }

  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', normalized)
  }
}

export function getLocaleOptions() {
  return localeMeta.map((locale) => ({
    ...locale,
    label: t(locale.labelKey),
  }))
}

export default {
  install(app) {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', i18nState.locale)
    }

    app.config.globalProperties.$i18n = i18nState
    app.config.globalProperties.$t = t
    app.config.globalProperties.$setLocale = setLocale
    app.config.globalProperties.$getLocaleOptions = getLocaleOptions
    app.provide('i18n', i18nState)
  },
}
